import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Pagination, Tabs, Tab, Button, Spinner, Form, InputGroup, Table, Alert } from 'react-bootstrap';
import { FaSearch, FaTimesCircle, FaEdit, FaUserEdit } from 'react-icons/fa';
import TableWrapper from '../../components/TableWrapper';
import TitleWithBack from '../../components/common/TitleWithBack';
import ReassignModal from './ReassignModal';
import EditTeamModal from './EditTeamModal';
import { useNavigate } from 'react-router-dom';

function AllocateProgress() {
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);
  const [seriesList, setSeriesList] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesError, setSeriesError] = useState(null);
  const [modules, setModules] = useState([]);
  const [readyForReview, setReadyForReview] = useState([]);
  const [groupedCompleted, setGroupedCompleted] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingReady, setLoadingReady] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editTeamAssignment, setEditTeamAssignment] = useState(null);

  const navigate = useNavigate();
  const rowsPerPage = 50;

  // Fetch exam assignments data
  const fetchData = (seriesId = selectedSeriesId) => {
    setLoading(true);
    const params = seriesId && seriesId !== '' ? { seriesId } : {};
    axios
      .get('/api/exam-assignments', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params
      })
      .then(response => {
        setModules(response.data);
        setLoading(false);
      })
      .catch(err => {
        setError('Error fetching progress data.');
        setLoading(false);
      });
  };

  // Fetch assignments ready for review
  const fetchReadyForReview = (seriesId = selectedSeriesId) => {
    setLoadingReady(true);
    const params = seriesId && seriesId !== '' ? { seriesId } : {};
    axios
      .get('/api/assignments/marked-done', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params
      })
      .then(res => {
        const data = res.data.map(item => ({ ...item, reviewable: true }));
        setReadyForReview(data);
        setLoadingReady(false);
      })
      .catch(err => {
        setError('Error fetching ready for review data.');
        setLoadingReady(false);
      });
  };


  // Group assignments by assignmentId (merging their uploads)
  const groupByAssignment = (arr) => {
    const grouped = {};
    arr.forEach(item => {
      const key = item.assignmentId;
      if (!grouped[key]) {
        grouped[key] = { ...item, uploads: [] };
      }
      if (item.uploads && Array.isArray(item.uploads)) {
        grouped[key].uploads = [...grouped[key].uploads, ...item.uploads];
      }
    });
    return Object.values(grouped);
  };

  // Fetch exam series list on mount
  useEffect(() => {
    setSeriesLoading(true);
    axios.get('/api/exam-series')
      .then(res => {
        setSeriesList(res.data);
        setSeriesLoading(false);
        // Default to 'Active Series' (no filter) if none selected
        if ((selectedSeriesId === null || selectedSeriesId === undefined)) {
          setSelectedSeriesId(''); // '' means no filter, backend returns all active series
        }
      })
      .catch(() => {
        setSeriesError('Failed to fetch exam series list');
        setSeriesLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchData(selectedSeriesId);
    fetchReadyForReview(selectedSeriesId);
    // eslint-disable-next-line
  }, [selectedSeriesId]);

  // Poll periodically so admin-side changes (e.g. a rescheduled exam date/
  // time) show up here without needing a manual refresh.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(selectedSeriesId);
      fetchReadyForReview(selectedSeriesId);
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedSeriesId]);

    // Helper to get examiner name from assignments (from either response)
    const getExaminerName = (m) => {
      if (m.examiners && m.examiners.length > 0) {
        // If there's a team leader and multiple examiners, show team leader first
        if (m.examiners.length > 1 && m.teamLeader) {
          const teamLeader = m.teamLeader;
          const otherExaminers = m.examiners.filter(examiner => examiner.id !== teamLeader.id);
          const otherNames = otherExaminers.map(examiner => examiner.fullName).join(', ');
          return otherNames ? `${teamLeader.fullName}, ${otherNames}` : teamLeader.fullName;
        }
        // For single examiner or no team leader info, show all examiners
        return m.examiners.map(examiner => examiner.fullName).join(', ');
      } else if (m.examinerName) {
        return m.examinerName;
      }
      return 'N/A';
    };

  const filteredModules = modules.filter(m => {
    const lowerSearch = searchText.toLowerCase();
    const examinerName = getExaminerName(m).toLowerCase();
  
    return (
      (m.moduleName && m.moduleName.toLowerCase().includes(lowerSearch)) ||
      (m.moduleCode && m.moduleCode.toLowerCase().includes(lowerSearch)) ||
      (m.examCentreName && m.examCentreName.toLowerCase().includes(lowerSearch)) ||
      examinerName.includes(lowerSearch)
    );
  });

  // In-progress assignments: ASSIGNED, ACCEPTED_BY_EXAMINER, or REJECTED_BY_EXAMINER.
  const inProgressRows = filteredModules.filter(m =>
    m.status === 'ASSIGNED' ||
    m.status === 'ACCEPTED_BY_EXAMINER' ||
    m.status === 'REJECTED_BY_EXAMINER'
  );

  // Completed assignments: those with status COMPLETED plus ready-for-review assignments.
  useEffect(() => {
    const completedFromModules = modules.filter(m => m.status === 'COMPLETED' || m.status === 'REJECTED');
    const combinedCompleted = [...completedFromModules, ...readyForReview];
  
    const lowerSearch = searchText.toLowerCase();
  
    const filteredCompleted = combinedCompleted.filter(m => {
      const examinerName = getExaminerName(m).toLowerCase();
      return (
        (m.moduleName && m.moduleName.toLowerCase().includes(lowerSearch)) ||
        (m.moduleCode && m.moduleCode.toLowerCase().includes(lowerSearch)) ||
        (m.examCentreName && m.examCentreName.toLowerCase().includes(lowerSearch)) ||
        examinerName.includes(lowerSearch)
      );
    });
  
    setGroupedCompleted(filteredCompleted);
  }, [modules, readyForReview, searchText]);
  

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };
    
  // Pagination for in-progress
  const [inProgressPage, setInProgressPage] = useState(1);
  const inProgressIndexOfLast = inProgressPage * rowsPerPage;
  const inProgressIndexOfFirst = inProgressIndexOfLast - rowsPerPage;
  const inProgressPaginated = inProgressRows.slice(inProgressIndexOfFirst, inProgressIndexOfLast);
  const inProgressTotalPages = Math.ceil(inProgressRows.length / rowsPerPage);

  // Pagination for completed
  const [completedPage, setCompletedPage] = useState(1);
  const completedIndexOfLast = completedPage * rowsPerPage;
  const completedIndexOfFirst = completedIndexOfLast - rowsPerPage;
  const completedPaginated = groupedCompleted.slice(completedIndexOfFirst, completedIndexOfLast);
  const completedTotalPages = Math.ceil(groupedCompleted.length / rowsPerPage);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'REJECTED_BY_EXAMINER':
        return <span className="badge bg-danger">Rejected</span>;
      case 'ACCEPTED_BY_EXAMINER':
        return <span className="badge bg-primary">Accepted</span>;
      case 'COMPLETED':
        return <span className="badge bg-success">Completed</span>;
      case 'ASSIGNED':
        return <span className="badge bg-info">Assigned</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  };

  // Navigation for uploading.
  const handleUpload = async (job) => {
    try {
      const response = await axios.get(`/api/modules/${job.examId}`);
      const moduleDetails = response.data;
      navigate(`/examiner/job/${job.id}/upload`, { state: { module: moduleDetails } });
    } catch (error) {
      setError("Error fetching module details for upload.");
    }
  };

  // Cancel handler
  const handleCancel = async (assignmentId, examinerId) => {
    try {
      await axios.post(
        `/api/exam-assignments/${assignmentId}/cancel?examinerId=${examinerId}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setModules(prev => prev.filter(m => m.id !== assignmentId));
    } catch (err) {
      setError('Error cancelling assignment.');
    }
  };

  const renderInProgressTable = () => {
    if (inProgressRows.length === 0) return <p className="mt-3">Nothing to show here</p>;
    return (
      <>
        <TableWrapper maxHeight="500px">
          <Table className="table table-striped table-hover mt-3">
            <thead>
              <tr>
                <th>#</th>
                <th>Exam/Module Name</th>
                <th>Code</th>
                <th>Exam Centre</th>
                <th>Examiner</th>
                <th>Action</th>
                <th>Assigned Date</th>
                <th>Status</th>
                <th>Comment</th>
                <th>Reschedule Reason</th>
              </tr>
            </thead>
            <tbody>
              {inProgressPaginated.map((m, idx) => (
                <tr key={m.id}>
                  <td>{inProgressIndexOfFirst + idx + 1}</td>
                  <td>{m.moduleName || 'N/A'}</td>
                  <td>{m.moduleCode || 'N/A'}</td>
                  <td>{m.examCentreName || 'N/A'}</td>
                  <td>{getExaminerName(m)}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {m.status === 'REJECTED_BY_EXAMINER' ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setSelectedAssignment(m);
                            setShowReassignModal(true);
                          }}
                        >
                          <FaEdit className="me-1" />
                          Re-Assign
                        </Button>
                      ) : (
                        (m.status === 'ASSIGNED' || m.status === 'ACCEPTED_BY_EXAMINER') && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleCancel(m.id, m.examiners && m.examiners.length ? m.examiners[0].id : null)}
                          >
                            <FaTimesCircle className="me-1" />
                            Cancel
                          </Button>
                        )
                      )}
                      {(m.status === 'ASSIGNED' || m.status === 'REJECTED_BY_EXAMINER' || m.status === 'ACCEPTED_BY_EXAMINER') && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setEditTeamAssignment(m);
                            setShowEditTeamModal(true);
                          }}
                        >
                          <FaUserEdit className="me-1" />
                          Edit Team
                        </Button>
                      )}
                    </div>
                  </td>
                  <td>{m.assignedDate ? formatDate(m.assignedDate) : '-'}</td>
                  <td>{getStatusBadge(m.status)}</td>
                  <td>{m.rejectionReason || '-'}</td>
                  <td
                    style={{ maxWidth: '200px', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                    title={m.examRescheduledAt ? new Date(m.examRescheduledAt).toLocaleString() : ''}
                  >
                    {m.examRescheduleReason || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        {inProgressTotalPages > 1 && (
          <Pagination className="mt-3">
            {[...Array(inProgressTotalPages)].map((_, pageIndex) => {
              const pageNumber = pageIndex + 1;
              return (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === inProgressPage}
                  onClick={() => setInProgressPage(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              );
            })}
          </Pagination>
        )}
      </>
    );
  };

  const renderCompletedTable = () => {
    if (groupedCompleted.length === 0) return <p className="mt-3">Nothing to show here</p>;
    return (
      <>
        <TableWrapper maxHeight="500px">
          <Table className="table table-bordered table-hover mt-3">
            <thead className="bg-light">
              <tr>
                <th>#</th>
                <th>Exam/Module Name</th>
                <th>Code</th>
                <th>Exam Centre</th>
                <th>Examiner</th>
                <th>Assigned Date</th>
                <th>Status</th>
                <th>Comment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {completedPaginated.map((m, idx) => {
                const assignmentIdValue = m.assignmentId || m.id || 1;
                const examCentreId = m.examCentreId || 1;
                return (
                  <tr key={m.assignmentId || idx}>
                    <td>{completedIndexOfFirst + idx + 1}</td>
                    <td>{m.moduleName || 'N/A'}</td>
                    <td>{m.moduleCode || 'N/A'}</td>
                    <td>{m.examCentreName || 'N/A'}</td>
                    <td>{getExaminerName(m)}</td>
                    <td>{m.assignedDate ? formatDate(m.assignedDate) : 'N/A'}</td>
                    <td>
                      {m.reviewable ? (
                        <span className="badge bg-warning">Ready for Review</span>
                      ) : (
                        getStatusBadge(m.status)
                      )}
                    </td>
                    <td>{m.rejectionReason || m.comment || 'N/A'}</td>
                    <td>
                      {m.reviewable ? (
                        <Button
                          variant="info"
                          size="sm"
                          style={{ color: '#fff' }}
                          onClick={() => navigate('/archiver/review', { state: { assignment: m } })}
                          className="me-1 mb-1"
                        >
                          Review &amp; Approve
                        </Button>
                      ) : ((m.status === 'COMPLETED' || m.status === 'REJECTED') ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled
                        >
                          No Action Needed
                        </Button>
                      ) : null
                    )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
        {completedTotalPages > 1 && (
          <Pagination className="mt-3">
            {[...Array(completedTotalPages)].map((_, pageIndex) => {
              const pageNumber = pageIndex + 1;
              return (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === completedPage}
                  onClick={() => setCompletedPage(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              );
            })}
          </Pagination>
        )}
      </>
    );
  };

  return (
    <div className="container-fluid">
      <TitleWithBack title="Allocation Progress" />
      <div className="d-flex flex-wrap justify-content-between align-items-center my-3">
        <div style={{ minWidth: 250 }}>
          <Form.Group controlId="examSeriesDropdown" className="mb-0">
            <Form.Label>Exam Series</Form.Label>
            {seriesLoading ? (
              <Spinner animation="border" size="sm" />
            ) : seriesError ? (
              <span className="text-danger">{seriesError}</span>
            ) : (
              <Form.Select
                value={selectedSeriesId === null || selectedSeriesId === undefined ? '' : selectedSeriesId}
                onChange={e => setSelectedSeriesId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Active Series</option>
                {seriesList.map(series => (
                  <option key={series.id} value={series.id}>
                    {series.name} ({series.startDate} - {series.endDate})
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
        </div>
        <InputGroup style={{ maxWidth: '300px' }}>
          <Form.Control placeholder="search" aria-label="Search" value={searchText} onChange={handleSearchChange} />
          <Button variant="outline-secondary">
            <FaSearch />
          </Button>
        </InputGroup>
      </div>
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {loading ? (
        <div className="d-flex align-items-center mt-4">
          <Spinner animation="border" variant="primary" className="me-2" />
          <span>Loading progress...</span>
        </div>
      ) : (
        <Tabs defaultActiveKey="inProgress" id="allocate-progress-tabs" className="mt-3">
          <Tab eventKey="inProgress" title="In-progress">
            {renderInProgressTable()}
          </Tab>
          <Tab eventKey="completed" title="Completed">
            {renderCompletedTable()}
          </Tab>
        </Tabs>
      )}
      {/* Reassign Modal for in-progress items */}
      {selectedAssignment && (
        <ReassignModal
          show={showReassignModal}
          onHide={() => {
            setShowReassignModal(false);
            setSelectedAssignment(null);
          }}
          assignmentId={selectedAssignment.id}
          oldExaminerId={
            selectedAssignment.examiners && selectedAssignment.examiners.length > 1
              ? (selectedAssignment.teamLeader ? selectedAssignment.teamLeader.id : selectedAssignment.examiners[0].id)
              : selectedAssignment.examiners && selectedAssignment.examiners.length
              ? selectedAssignment.examiners[0].id
              : undefined
          }
          oldExaminerName={
            selectedAssignment.examiners && selectedAssignment.examiners.length > 1
              ? (selectedAssignment.teamLeader ? selectedAssignment.teamLeader.fullName : selectedAssignment.examiners[0].fullName)
              : selectedAssignment.examiners && selectedAssignment.examiners.length
              ? selectedAssignment.examiners[0].fullName
              : ''
          }
          onReassignSuccess={fetchData}
        />
      )}
      <EditTeamModal
        show={showEditTeamModal}
        onHide={() => {
          setShowEditTeamModal(false);
          setEditTeamAssignment(null);
        }}
        assignment={editTeamAssignment}
        onSuccess={fetchData}
      />
    </div>
  );
}

export default AllocateProgress;
